/**
 * Persistent storage: asks the browser not to evict this origin's data.
 *
 * By default a site's storage is "best-effort": the browser may delete it
 * without asking (to free disk space, or Safari's 7-day rule for sites used in
 * a tab). Once `navigator.storage.persist()` is granted, only the user can
 * clear it. How the grant is decided depends on the browser: Chrome and Edge
 * decide silently (an installed app is almost always granted), Firefox asks
 * the user, Safari grants it mostly to Home Screen apps. Asking again after a
 * refusal is harmless, so callers ask on every signed-in session and again
 * when the app is installed.
 */
import { createLogger, LogLevel } from './logger';

const logger = createLogger('StoragePersistence', LogLevel.INFO);

/** Fired on window after a request may have changed the persisted state. */
export const STORAGE_PERSISTENCE_CHANGED = 'storage-persistence-changed';

/** Whether this origin's storage is persistent; null when the browser cannot say. */
export async function isStoragePersisted(): Promise<boolean | null> {
  if (typeof navigator === 'undefined' || !navigator.storage?.persisted) return null;
  try {
    return await navigator.storage.persisted();
  } catch (error) {
    logger.warn('navigator.storage.persisted() failed', error);
    return null;
  }
}

/**
 * Ask for persistent storage unless it is already granted. Resolves with the
 * resulting state; never throws.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.storage?.persist) return false;
  try {
    if (await navigator.storage.persisted()) return true;
    const granted = await navigator.storage.persist();
    logger.info(`Persistent storage ${granted ? 'granted' : 'not granted'}`);
    window.dispatchEvent(new Event(STORAGE_PERSISTENCE_CHANGED));
    return granted;
  } catch (error) {
    logger.warn('navigator.storage.persist() failed', error);
    return false;
  }
}
